import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    ConfigurationService,
    MetadataBuilder,
    NavigationService,
    UrlService,
    OrderService,
    PathsService,
    PortalConfigurationService
} from '@draeger-academy/common';
import { HubspotTrackingService } from '@draeger-academy/presentation';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ContentPageMetadataProvider } from './content-page-metadata.provider';
import { MaintenanceOverlayService } from '@draeger-academy/presentation/lib/modules/maintenance-overlay';

@Component({
    selector: 'da-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private _$loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$isNotError500Page: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$isNotCountrySelectionPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _subscriptionSiteSettings: Subscription;
    private _$showHeader = new BehaviorSubject<boolean>(false);
    private _$showFooter = new BehaviorSubject<boolean>(false);
    private _$showBreadcrumbs = new BehaviorSubject<boolean>(false);
    private _$showContact = new BehaviorSubject<boolean>(false);
    private _$showCookieNotification = new BehaviorSubject<boolean>(false);
    private _$maintenanceOverlay = new BehaviorSubject<boolean>(false);
    private _$cookieConsent = new BehaviorSubject<boolean>(false);
    private _$isOnlyDomain = new BehaviorSubject<boolean>(false);
    public portalHubSpotId: string;
    public portalImprintUrl: string;
    public portalId: string;

    constructor(
        private _navigationService: NavigationService,
        private _configurationService: ConfigurationService,
        private _translateService: TranslateService,
        private _metadataBuilder: MetadataBuilder,
        private _router: Router,
        private _cookieService: CookieService,
        private _urlService: UrlService,
        private _orderService: OrderService,
        private _pathsService: PathsService,
        private _hubspotTrackingService: HubspotTrackingService,
        private _maintenanceOverlayService: MaintenanceOverlayService,
        private _portalConfigurationService: PortalConfigurationService
    ) {
        if (window.location.pathname === '/' && !this._cookieService.get('SiteId') && !this._configurationService.isPartnerPage()) {
            this._$isOnlyDomain.next(true);
            this._router.navigate(['/countryselection']);
        }
    }

    public get loaded$(): Observable<boolean> {
        return this._$loaded.asObservable();
    }

    public get isNotError500Page$(): Observable<boolean> {
        return this._$isNotError500Page.asObservable();
    }

    public get isNotCountrySelectionPage$(): Observable<boolean> {
        return this._$isNotCountrySelectionPage.asObservable();
    }

    public get showHeader$(): Observable<boolean> {
        return this._$showHeader.asObservable();
    }

    public get showFooter$(): Observable<boolean> {
        return this._$showFooter.asObservable();
    }

    public get showBreadcrumbs$(): Observable<boolean> {
        return this._$showBreadcrumbs.asObservable();
    }

    public get showContact$(): Observable<boolean> {
        return this._$showContact.asObservable();
    }

    public get showCookieNotification$(): Observable<boolean> {
        return this._$showCookieNotification.asObservable();
    }
    public get maintenanceOverlay$(): Observable<boolean> {
        return this._$maintenanceOverlay.asObservable();
    }
    public get cookieConsent$(): Observable<boolean> {
        return this._$cookieConsent.asObservable();
    }

    public get isOnlyDomain$(): Observable<boolean> {
        return this._$isOnlyDomain.asObservable();
    }

    ngOnDestroy(): void {
        let _site = this._configurationService.getCurrentSite();
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    _site = portalId;
                }
            });
        }

        if (this._subscriptionSiteSettings) {
            this._subscriptionSiteSettings.unsubscribe();
        }
        this._cookieService.delete(`order${_site}`);
    }

    ngOnInit() {
        if (!sessionStorage.getItem('portalLang')) {
            sessionStorage.setItem('portalLang', JSON.stringify({ portalId: 'de-ext-sd', lang: 'de' }));
        }
        const _isPartnerPortal = window.location.href.indexOf('partner') >= 0;
        if (_isPartnerPortal) {
            this._translateService.setDefaultLang('partner');
            this._translateService.use('partner');
            this._$isNotError500Page.next(true);
            this._$isNotCountrySelectionPage.next(true);
            this._loadPartnerPortalData();
        } else {
            this._$isNotError500Page.next(window.location.href.indexOf('server-error') < 0);
            const _isNoSelectionPage = !this._configurationService.isSelectionPage();
            this._$isNotCountrySelectionPage.next(_isNoSelectionPage);
            if (!_isNoSelectionPage) {
                this._translateService.use('en_uk');
            }
            this._loadApplicationData(_isNoSelectionPage);
        }
        const storedDate = localStorage.getItem('storedDate') ? this._urlService.decode(localStorage.getItem('storedDate')) : null;
        if (!storedDate && storedDate !== '') {
            const currentDate = new Date();
            localStorage.setItem('storedDate', this._urlService.encode(JSON.stringify(currentDate.toString())));
        }
        sessionStorage.removeItem('trackingScript');
    }

    private _loadApplicationData(_isNoSelectionPage: boolean) {
        const prom1 = this._navigationService._loadAllNavigationData();
        const prom2 = this._configurationService.loadConfiguration(false);
        Promise.all([prom1, prom2]).then(() => {
            this._subscriptionSiteSettings = this._configurationService.$siteSettings.subscribe((settings) => {
                if (settings) {
                    const _metaData = settings.filter((setting) => setting.key.toLowerCase() === 'metadata');
                    if (_metaData && _metaData[0] && _isNoSelectionPage) {
                        const _title = _metaData[0].value.filter((meta) => meta.key.toLowerCase() === 'pagetitle')[0];
                        this._metadataBuilder.build(new ContentPageMetadataProvider({ title: _title.value || 'Dräger Online Academy' }));
                    } else if (_metaData && _metaData[0] && !_isNoSelectionPage) {
                        this._metadataBuilder.build(new ContentPageMetadataProvider({ title: 'Dräger Online Academy' }));
                    }
                    this._setFeatures(settings, _isNoSelectionPage);
                    this._hubspotTrackingService.loadTrackingScript('');
                    if (this._$maintenanceOverlay.getValue() && !sessionStorage.getItem('maintenanceOverlay')) {
                        const _site = this._configurationService.getCurrentSite();
                        this.loadMaintenanceOverlay(_site, false);
                    }
                }
            });
            const _site = this._configurationService.getCurrentSite();
            this._configurationService.getCountriesAndSalutationsByLanguage(_site.split('_')[0]);
        });

        // Deleting local storage related to the order after 12h
        const storedDate = localStorage.getItem('storedDate') ? this._urlService.decode(localStorage.getItem('storedDate')) : null;
        if (storedDate) {
            const diffDays = (date, otherDate) => Math.ceil(Math.abs(date - otherDate) / (1000 * 60 * 60));
            const diff = diffDays(new Date(), new Date(storedDate.replace(/^"(.*)"$/, '$1').toString()));
            if (diff > 12) {
                this._orderService.cancleOrder();
                localStorage.clear();
            }
        }
    }

    private _loadPartnerPortalData() {
        this._configurationService.loadConfiguration(true).then(() => {
            this._subscriptionSiteSettings = this._configurationService.$siteSettings.subscribe((settings) => {
                if (settings) {
                    this._setFeatures(settings, true);

                    this._configurationService.currentPortal$.subscribe((portalId) => {
                        if (portalId) {
                            this.portalId = portalId;
                            this._portalConfigurationService.loadPortalPreferences(portalId).then((preferences) => {
                                this.portalHubSpotId = preferences.hubSpotId;
                                this.portalImprintUrl = preferences.imprintUrl;
                            });
                            this._configurationService.getConfigPartnerByPortal().then((values) => {
                                if (values) {
                                    const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');

                                    let _cookieConsentShow;
                                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                                        _cookieConsentShow = tempFeatures[0].value.filter(
                                            (setting) => setting.name === 'ActiveApplications'
                                        );
                                        this._$cookieConsent.next(_cookieConsentShow[0].enabled);
                                    }
                                    let _maintenanceOverlayShow;
                                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                                        _maintenanceOverlayShow = tempFeatures[0].value.filter(
                                            (setting) => setting.name === 'MaintenanceOverlay'
                                        );
                                        if (_maintenanceOverlayShow.length > 0) {
                                            this._$maintenanceOverlay.next(_maintenanceOverlayShow[0].enabled);
                                            if (this._$maintenanceOverlay.getValue() && !sessionStorage.getItem('maintenanceOverlay')) {
                                                this.loadMaintenanceOverlay(portalId, true);
                                            }
                                        }
                                    }
                                }
                            });
                        }
                    });
                }
            });
        });
    }
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
    private _setFeatures(settings: any, _isNoSelectionPage: boolean) {
        const _feature = settings.filter((setting) => setting.key.toLowerCase() === 'features');
        if (_feature && _feature[0]) {
            const _header = _feature[0].value.filter((data) => data.name.toLowerCase() === 'header')[0];
            this._$showHeader.next(!_isNoSelectionPage || (_header && _header.enabled));

            const _footer = _feature[0].value.filter((data) => data.name.toLowerCase() === 'footer')[0];
            this._$showFooter.next(!_isNoSelectionPage || (_footer && _footer.enabled));

            const _contact = _feature[0].value.filter((data) => data.name.toLowerCase() === 'contact')[0];
            this._$showContact.next(_contact && _contact.enabled);

            const _breadcrumbs = _feature[0].value.filter((data) => data.name.toLowerCase() === 'breadcrumbs')[0];
            this._$showBreadcrumbs.next(_breadcrumbs && _breadcrumbs.enabled);

            const _cookie = _feature[0].value.filter((data) => data.name.toLowerCase() === 'cookie')[0];
            this._$showCookieNotification.next(_isNoSelectionPage && _cookie && _cookie.enabled);
            const _maintenanceOverlay = _feature[0].value.filter((data) => data.name.toLowerCase() === 'maintenanceoverlay')[0];
            this._$maintenanceOverlay.next(_isNoSelectionPage && _maintenanceOverlay && _maintenanceOverlay.enabled);
            const _cookieConsent = _feature[0].value.filter((data) => data.name.toLowerCase() === 'activeapplications')[0];
            this._$cookieConsent.next(_isNoSelectionPage && _cookieConsent && _cookieConsent.enabled);
        }
        this._pathsService.initPathParameters();
        this._$loaded.next(true);
    }

    private loadMaintenanceOverlay(siteOrPortal, isPortal) {
        if (!isPortal) {
            if (this._cookieService.get('dra_cookie_consent_allowed_' + siteOrPortal) || !this._$cookieConsent.getValue()) {
                this._maintenanceOverlayService.openMaintenanceOverlay();
            }
        } else {
            if (this._$maintenanceOverlay.getValue()) {
                if (this._cookieService.get('dra_cookie_consent_allowed_' + siteOrPortal) || !this._$cookieConsent.getValue()) {
                    this._maintenanceOverlayService.openMaintenanceOverlay();
                }
            }
        }
    }
    closeCookieOverlay(value) {
        if (value) {
            if (this._$maintenanceOverlay.getValue()) {
                this._maintenanceOverlayService.openMaintenanceOverlay();
            }
        }
    }
}
